import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { SampleSet } from "@/services/backend/sample-set/sample-set";
import { useSampleSetListQuery } from "@/services/backend/sample-set/service";
import { H4 } from "@/shared/components/typography";
import { SampleType } from "@/services/backend/types";
import { CollapsibleDataTable } from "@/shared/components/collapsible-data-table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { CommentInputComponent } from "./comment-component";
import SetContentLabel from "./set-content-label";
import SampleDetailRow from "./sample-detail-row";
import SampleSetMetadataRow from "./metadata-row";
import SwitchProcedureModal from "./switch-procedure-modal";

const createTableColumns = (immutable?: boolean): ColumnDef<SampleSet>[] => [
  {
    id: "sampleSetNumber",
    accessorKey: "sampleSetNumber",
    header: "Probensetnummer",
    cell: ({ row }) => {
      const sampleSetData = row.original;
      return sampleSetData.sampleSetNumber;
    },
  },
  {
    id: "procedureType",
    accessorKey: "procedureType",
    header: "Verfahren",
    cell: ({ row }) => {
      const sampleSetData = row.original;
      return <p>{sampleSetData.procedureType}</p>;
    },
  },
  {
    id: "samplingVolume",
    accessorKey: "samplingVolume",
    header: "Probevolumen",
    cell: ({ row }) => {
      const sampleSetData = row.original;

      if (sampleSetData.sampleType === SampleType.AirMicrobial) {
        return <p>{sampleSetData.samplingVolume} L</p>;
      }
      return <p>Oberflächenkeimprobe</p>;
    },
  },
  {
    id: "comment",
    accessorKey: "comment",
    header: "Kommentar",
    cell: ({ row }) => {
      const sampleSetData = row.original;
      return (
        <CommentInputComponent
          sampleSet={sampleSetData}
          immutable={immutable}
        />
      );
    },
  },
  {
    id: "externalComment",
    accessorKey: "externalComment",
    header: "Externer Kommentar",
    cell: ({ row }) => {
      const sampleSetData = row.original;
      return sampleSetData.externalComment == null ? (
        <p>Kein Kommentar hinterlegt</p>
      ) : (
        <p>{sampleSetData.externalComment}</p>
      );
    },
  },
  {
    id: "content",
    accessorKey: "id",
    header: "Inhalt",
    cell: ({ row }) => {
      const sampleSetData = row.original;
      return <SetContentLabel samples={sampleSetData.samples} />;
    },
  },
  {
    id: "actions",
    accessorKey: "id",
    header: "Aktionen",
    cell: ({ row }) => {
      const sampleSetData = row.original;
      if (sampleSetData.switchedOut) {
        return (
          <span className="text-muted-foreground text-sm italic">
            Gewechselt (Abweichung)
          </span>
        );
      }
      return (
        <SwitchProcedureModal sampleSet={sampleSetData} disabled={immutable} />
      );
    },
  },
];

interface SampleSetTableProps {
  ownerId: string;
  ownerType: string;
  sampleType: SampleType;
  label: string;
  visibilityState?: VisibilityState;
  immutable?: boolean;
}

export function SampleSetTable({
  ownerId,
  ownerType,
  sampleType,
  label,
  visibilityState = {},
  immutable,
}: SampleSetTableProps) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useSampleSetListQuery({
    ownerId: {
      active: true,
      values: [ownerId],
    },
    ownerType: {
      active: true,
      values: [ownerType],
    },
    sampleType: {
      active: true,
      values: [sampleType],
    },
    limit: 0,
    offset: 0,
  });

  const cols = createTableColumns(immutable);
  const table = useDataTableController(
    cols,
    list?.items || [],
    visibilityState,
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || isFetching) {
    return (
      <DataTableContainer className="w-full">
        <CollapsibleDataTable
          table={table}
          columns={cols}
          collapsibleTriggerId="sampleSetNumber"
          renderCollapsibleContent={() => null}
          isLoading
        />
      </DataTableContainer>
    );
  }

  if (!list || !list.items || list.items.length === 0) {
    return <div />;
  }

  return (
    <div className="flex w-full flex-col space-y-4">
      <Collapsible open={metadataOpen} onOpenChange={setMetadataOpen}>
        <CollapsibleTrigger className="flex flex-row items-center space-x-1">
          <H4>{label}</H4>
          {metadataOpen ? <ChevronDown /> : <ChevronRight />}
        </CollapsibleTrigger>
        <CollapsibleContent>
          {list != null && list.items.length > 0 && (
            <SampleSetMetadataRow samplesets={list.items} />
          )}
        </CollapsibleContent>
      </Collapsible>

      <DataTableContainer className="w-full">
        <CollapsibleDataTable
          table={table}
          columns={cols}
          collapsibleTriggerId="sampleSetNumber"
          renderCollapsibleContent={(sampleSet: SampleSet) => (
            <SampleDetailRow samples={sampleSet.samples} />
          )}
        />
      </DataTableContainer>
    </div>
  );
}
